import {
    CoordinateQuestionOptions,
    isRationalQuestion,
    MissingNumberOptions,
    ProblemFormatOptions,
    RatioQuestionOptions,
    RationalKindOptions,
} from "../Constants.ts";
import { ISettingsProps } from "../Models.ts";

/**
 * Whether a sheet can be shown on screen (ADR 0091 §3). Kept apart from
 * `presentCards.ts` so the button can ask without pulling the generators in:
 * the cards are built, and the view loaded, only when it is pressed.
 */

/**
 * Formats the PDF draws with `GeneratePromptAndAnswerProblem`: `display[0]` is
 * the whole prompt, already ending in "=" or in the words that lead to the
 * blank, and `display[1]` is the answer (ADR 0032).
 */
export const PROMPT_AND_ANSWER: ReadonlySet<ProblemFormatOptions> = new Set([
    ProblemFormatOptions.PlaceValue,
    ProblemFormatOptions.NumberProperties,
    ProblemFormatOptions.Percent,
    ProblemFormatOptions.Ratio,
    ProblemFormatOptions.Averages,
    ProblemFormatOptions.Geometry,
    ProblemFormatOptions.RomanNumerals,
]);

/**
 * Formats whose blank sits inside the line, not after it: a missing term in a
 * sequence, a symbol between two integers, a unit after a converted amount, a
 * variable the answer is written against. Each has its own PDF renderer, and its
 * card is built to print the same text (ADR 0091 §3, as built).
 */
export const IN_LINE_BLANK: ReadonlySet<ProblemFormatOptions> = new Set([
    ProblemFormatOptions.NumberPatterns,
    ProblemFormatOptions.Integers,
    ProblemFormatOptions.UnitConversion,
    ProblemFormatOptions.Algebra,
]);

/** Every format the view can show. */
export function canPresentFormat(format: ProblemFormatOptions): boolean {
    return (
        format === ProblemFormatOptions.MathFacts ||
        format === ProblemFormatOptions.OrderOfOperations ||
        PROMPT_AND_ANSWER.has(format) ||
        IN_LINE_BLANK.has(format)
    );
}

/**
 * Whether the button is offered for a sheet as configured.
 *
 * A differentiation set and a class set are several sheets in one PDF, and the
 * view shows one: offering it would put the middle tier on the board and hide
 * the other two. Missing-number sheets and word problems are math facts and
 * math-facts-shaped but print differently from their numbers, so they are out
 * with the formats that draw.
 */
export function canPresent(
    settings: ISettingsProps,
    several: { differentiationSet: boolean; classSet: boolean }
): boolean {
    if (several.differentiationSet || several.classSet) {
        return false;
    }
    // Slope from Two Points is a sentence and an answer; Slope from a Graph has
    // no text prompt, and the rest of the Coordinate Plane draws (ADR 0126).
    const slopeFromPoints =
        settings.Format === ProblemFormatOptions.CoordinatePlane &&
        settings.CoordinateQuestion === CoordinateQuestionOptions.SlopeFromPoints;
    if (!slopeFromPoints && !canPresentFormat(settings.Format)) {
        return false;
    }
    // A table or graph question points at a drawing the cards do not carry: "is
    // y proportional to x" with no table, "use the graph" with no graph (ADR 0104,
    // 0105).
    if (
        settings.Format === ProblemFormatOptions.Ratio &&
        (settings.RatioQuestion === RatioQuestionOptions.ProportionalGraphs ||
            settings.RatioQuestion === RatioQuestionOptions.Proportional)
    ) {
        return false;
    }
    // The Fractions format is left out because it draws stacked fractions, and a
    // rational sheet that asks for fractions draws them the same way: the room
    // would read `-3/4 + (-1/2)` where the sheet prints a stack (ADR 0091 §3,
    // ADR 0122 §5). Decimals print as one line of text and stay.
    if (
        settings.Format === ProblemFormatOptions.Integers &&
        isRationalQuestion(settings.IntegerQuestion) &&
        settings.RationalKind !== RationalKindOptions.Decimals
    ) {
        return false;
    }
    return !(
        settings.Format === ProblemFormatOptions.MathFacts &&
        settings.MissingNumber &&
        settings.MissingNumber !== MissingNumberOptions.Off
    );
}
